/**
 * v1.6.1687 — Asana 1216892546346406: show WHICH permissions are missing.
 *
 * The API has answered 403 with the missing permission named since mid-2026:
 *
 *     { "status": false,
 *       "message": "You do not have permission to perform this action. Missing permission: order.viewAny",
 *       "data": null,
 *       "errors": { "required_permissions": ["order.viewAny"] } }
 *
 * v1.6.1273 taught `extractError` to render that, which is why some pages
 * (abandoned carts) show a proper notice today. But a per-page handler only
 * fires if the page's handler runs at all, and on the orders page it does not —
 * see THE ACTUAL BUG below. So the 403 is now also caught centrally, once, as
 * the ticket asks.
 *
 * ── THE ACTUAL BUG ANGELO RE-VERIFIED ──────────────────────────────────────
 *
 * The orders page's first request is not `/orders`. It is
 * `GET /stores?per_page=100`, via StoreContextService. When that 403s:
 *
 *   1. StoreContextService logs a warning; `context$` never emits a store id.
 *   2. orders-list gates BOTH of its load triggers on a store id being
 *      present, so `load()` is never called.
 *   3. `loading` is initialised to `true` at the field declaration.
 *   4. The skeleton renders forever and NO error handler ever runs.
 *
 * Hence "no error message, only the old loading placeholders". The notice alone
 * would not have fixed it: there was nothing to attach a message to, because
 * nothing failed — the page simply never started. `storeLoadFailed` on
 * StoreContextService is the other half of this fix.
 *
 * ── WHAT A 403 IS NOT ──────────────────────────────────────────────────────
 *
 * - A BILLING BLOCK is also a 403, told apart only by `data.reason`. It must
 *   keep showing the billing screen, so it is excluded here (and the existing
 *   BillingInterceptor still handles it).
 * - A 403 with neither `required_permissions` nor a `reason` — a disabled
 *   plugin, say — is shown as the server worded it, with no permission chips
 *   invented for it.
 * - A 403 is NEVER a logout. Only 401 means the session is over, which the
 *   api-log interceptor already handles; nothing here touches auth.
 */

import { isExportSwitchForbidden } from './export-switch.utils';

export interface PermissionDenial {
  /** Request URL, so a page can tell its main request from a side one. */
  url: string;
  /** The server's own message, shown as sent. */
  message: string;
  /** Permission keys from `errors.required_permissions`. May be empty. */
  permissions: string[];
}

/**
 * A permission 403, as opposed to a billing block or any other status.
 *
 * A billing 403 carries `data.reason`; a permission 403 does not. Getting this
 * backwards turns every ordinary denial into a full-screen billing lock-out,
 * which is why it is a single explicit test rather than a status check.
 */
export function isPermissionForbidden(err: any): boolean {
  if (Number(err?.status) !== 403) return false;
  const reason = err?.error?.data?.reason;
  // A billing block — not ours.
  if (typeof reason === 'string' && reason.length > 0) return false;
  // v1.6.1814 — an EXPORT SWITCH refusal (Asana 1219882772481516) is not ours
  // either. It carries `data.switch` the way a billing block carries
  // `data.reason`. Claimed here, it would tell the operator to "ask an
  // administrator to add the missing permission to your role" — advice nobody
  // can act on, because no permission is missing: the platform team switched
  // the tenant's exports off.
  if (isExportSwitchForbidden(err)) return false;
  return true;
}

/** The permission keys named by a 403, or an empty array. */
export function readRequiredPermissions(err: any): string[] {
  const raw = err?.error?.errors?.required_permissions;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const p of raw) {
    const s = String(p ?? '').trim();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

/**
 * The server's message, stripped of its trailing "Missing permission: x" tail.
 *
 * The tail is removed ONLY when the keys are available to render separately as
 * chips — otherwise the operator would read the same permission twice. With no
 * keys the message is left exactly as sent, because then the tail is the only
 * place the information appears.
 */
export function readDenialMessage(err: any): string {
  const raw = String(err?.error?.message ?? '').trim();
  if (!raw) return 'You do not have permission to perform this action.';
  if (readRequiredPermissions(err).length) {
    const head = raw.split(/Missing permissions?:/i)[0]?.trim();
    if (head) return head;
  }
  return raw;
}

export function readDenial(err: any, url = ''): PermissionDenial {
  return {
    url: String(url || err?.url || ''),
    message: readDenialMessage(err),
    permissions: readRequiredPermissions(err),
  };
}

/**
 * Merge a denial into the list for the current page.
 *
 * Several requests on one page can each 403, and the ticket is explicit: ONE
 * notice listing every missing permission, not one toast per request. So
 * denials accumulate and are de-duplicated by URL — a page that retries the
 * same failing request must not grow the list each time.
 */
export function mergeDenial(
  existing: PermissionDenial[],
  denial: PermissionDenial,
): PermissionDenial[] {
  const i = existing.findIndex(d => d.url === denial.url);
  if (i > -1) {
    const next = [...existing];
    next[i] = denial;
    return next;
  }
  return [...existing, denial];
}

/** Every distinct permission across the page's denials, in first-seen order. */
export function allMissingPermissions(denials: PermissionDenial[]): string[] {
  const out: string[] = [];
  for (const d of denials ?? []) {
    for (const p of d.permissions) if (!out.includes(p)) out.push(p);
  }
  return out;
}

/**
 * The single sentence for the notice.
 *
 * The server's own message is preferred and shown as sent — it is already a
 * complete sentence and the ticket says not to write our own text. Only when
 * several requests failed with DIFFERENT messages is a neutral summary used,
 * because stacking three server sentences reads worse than one plus the chips.
 */
export function denialHeadline(denials: PermissionDenial[]): string {
  const messages: string[] = [];
  for (const d of denials ?? []) {
    const m = d.message?.trim();
    if (m && !messages.includes(m)) messages.push(m);
  }
  if (messages.length === 1) return messages[0];
  if (messages.length > 1) return 'You do not have permission to see everything on this page.';
  return 'You do not have permission to perform this action.';
}

/**
 * True when there is nothing to name — a disabled plugin, say. The message is
 * still shown; only the chips are omitted, since inventing a permission key
 * would send someone hunting for a role setting that does not exist.
 */
export function hasNamedPermissions(denials: PermissionDenial[]): boolean {
  return allMissingPermissions(denials).length > 0;
}

export const ASK_ADMIN_NOTE =
  'Ask an administrator to add the missing permission to your role.';

/**
 * Split a permission key for display: `order.viewAny` → "Orders · view any".
 *
 * The raw key is kept alongside, because the raw key is what an administrator
 * types into the role editor — a prettified version alone would be unusable
 * for the thing the operator has to go and ask for.
 */
export function describePermission(key: string): string {
  const s = String(key ?? '').trim();
  if (!s.includes('.')) return s;
  const [group, action] = s.split('.');
  const words = String(action ?? '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
  const subject = group.replace(/_/g, ' ');
  return `${subject} · ${words}`.trim();
}

/**
 * Is this denial the page's MAIN request, or a side one?
 *
 * The ticket distinguishes them: a failed main request replaces the list with
 * an empty state carrying the message; a failed side request (the store filter,
 * say) leaves the rest of the page working and shows the notice only on that
 * part. The page knows its own main endpoint, so it passes a fragment to match.
 */
export function denialFor(denials: PermissionDenial[], urlFragment: string): PermissionDenial | null {
  const frag = String(urlFragment ?? '').trim();
  if (!frag) return null;
  return (denials ?? []).find(d => d.url.includes(frag)) ?? null;
}

/**
 * Requests whose 403 must NOT raise a page notice.
 *
 * A permission probe is a request whose whole purpose is to find out whether
 * the user may do something; its 403 is the answer, not a fault. Surfacing it
 * would put a permission banner on a page that is working exactly as intended.
 */
export const SILENT_403_PATHS = [
  '/permissions',
  '/me',
  '/billing/status',
];

export function isSilent403(url: any): boolean {
  const u = String(url ?? '');
  return SILENT_403_PATHS.some(p => u.includes(p));
}

// ── v1.6.1771 — Asana 1219658573529681 ──────────────────────────────────────
//
// "The internal permission key (product_variant.viewAny) is shown directly to
// the end user. To a non-technical user this looks like a system error."
//
// v1.6.1687 showed the key on purpose: it is what an administrator types into
// the role editor, so naming it saved a round of "which permission?". That
// reasoning holds for the person who can GRANT the permission and for nobody
// else. The person who hits the wall usually cannot act on a key at all — they
// have to go and ask someone — so what they need is a sentence about the page
// they were trying to open.
//
// So the audience decides: a plain sentence for everyone, the raw keys only
// for an administrator who can use them.

/** Permission verbs, in the words an operator would use. */
const ACTION_WORDS: Record<string, string> = {
  viewany:     'view',
  view:        'view',
  create:      'add',
  store:       'add',
  update:      'edit',
  edit:        'edit',
  delete:      'delete',
  destroy:     'delete',
  forcedelete: 'permanently delete',
  restore:     'restore',
  export:      'export',
  import:      'import',
  approve:     'approve',
  assign:      'assign',
  run:         'run',
  sync:        'sync',
};

/**
 * `product_variant` → "Product Variants".
 *
 * Plural because every one of these names a section of the panel, which is
 * what the operator was trying to open. The plural is the naive one: these
 * keys are snake_case nouns from the API, not free text, and a word list for
 * the handful of irregulars would go stale the moment the API adds one.
 */
export function permissionSubject(group: string): string {
  const words = String(group ?? '').replace(/_/g, ' ').trim();
  if (!words) return 'this';
  const titled = words.replace(/\b[a-z]/g, c => c.toUpperCase());
  if (/(s|x|z|ch|sh)$/i.test(titled)) return titled + 'es';
  if (/[^aeiou]y$/i.test(titled)) return titled.slice(0, -1) + 'ies';
  return titled + 's';
}

/**
 * One sentence an operator can act on: what they cannot do, and to what.
 *
 * Returns '' for a key it cannot parse, so the caller falls back to the
 * server's own message rather than printing half a sentence.
 */
export function friendlyDenialSentence(permissions: readonly string[]): string {
  const keys = (permissions ?? []).map(k => String(k ?? '').trim()).filter(Boolean);
  if (!keys.length) return '';

  const parts: string[] = [];
  for (const key of keys) {
    if (!key.includes('.')) continue;
    const [group, action] = key.split('.');
    const verb = ACTION_WORDS[String(action ?? '').toLowerCase()] ?? String(action ?? '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
    const subject = permissionSubject(group);
    const phrase = `${verb} ${subject}`;
    if (!parts.includes(phrase)) parts.push(phrase);
  }
  if (!parts.length) return '';

  if (parts.length === 1) return `You don't have access to ${parts[0]}.`;
  const last = parts[parts.length - 1];
  return `You don't have access to ${parts.slice(0, -1).join(', ')} or ${last}.`;
}

/**
 * The headline the notice shows, given who is reading it.
 *
 * An administrator keeps the server's own sentence, which is precise and
 * matches what they will see in logs. Everyone else gets the friendly one —
 * and only when the keys could be parsed into it, so a denial we cannot
 * describe still says something true rather than something vague.
 */
export function denialHeadlineFor(
  denials: PermissionDenial[],
  isAdmin: boolean,
): string {
  if (isAdmin) return denialHeadline(denials);
  return friendlyDenialSentence(allMissingPermissions(denials)) || denialHeadline(denials);
}

/** What to tell the reader to do next. Only an admin can act on the key. */
export function denialNextStep(isAdmin: boolean): string {
  return isAdmin
    ? ASK_ADMIN_NOTE
    : 'Ask an administrator to give your role access, and they can grant it from the role editor.';
}

/**
 * ── v1.6.1787 — a denial the user can do nothing about (Asana 1219658573529681)
 *
 * Angelo: "Still randomly get the pop up at the top telling me i dont have
 * permissions even when i have all perimssions."
 *
 * A 403 can arrive for reasons unrelated to the operator's role — a feature
 * not enabled for the tenant, a request that did not scope to a store, a
 * backend check that is wrong — and the notice reported all of them as "ask
 * an administrator for this permission". To a super admin, who holds
 * everything by definition, that is impossible advice about a permission they
 * already have.
 *
 * `holds` is the caller's own check (PermissionService.has), kept as an
 * argument so this stays pure and testable.
 *
 * A denial naming NO permission is NOT suppressed: it carries the server's
 * own sentence, which may be the only account of what went wrong.
 */
export function deniedPermissionsAlreadyHeld(
  permissions: readonly string[] | null | undefined,
  holds: (permission: string) => boolean,
): boolean {
  const named = permissions ?? [];
  if (!named.length) return false;
  return named.every(p => holds(p));
}
